'use client';

import { SingleValue } from 'react-select';
import { Suspense } from 'react';

import './css/search.css';
import { TDevice, TSelectOption } from '@/types';
import { SearchDesktop } from './SearchDesktop';
import { SearchMobile } from './SearchMobile';
import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { slugFromLabel } from '@/lib/tour-slug';
import { useSearchQuery } from './hooks/useSearchQuery';

export type TSearchProps = {
  activityList?: Array<TSelectOption>;
  selectedActivity: TSelectOption | null;
  handleSelectActivity: (activity: SingleValue<TSelectOption>, device?: TDevice) => void;
  locationList: Array<TSelectOption>;
  selectedLocation: TSelectOption | null;
  handleSelectLocation: (location: SingleValue<TSelectOption>, device?: TDevice) => void;
  locationsLoading?: boolean;
  activitiesLoading?: boolean;
  allActivities?: Array<TSelectOption>;
};

export const allActivitiesOption = { label: 'All activities', value: 0, slug: '' };
export const allLocationsOption = { label: 'All locations', value: 0, slug: '' };

const SearchContent = () => {
  const searchParams = useSearchParams();
  const isPreselectedRef = useRef<null | boolean>(null);
  const [selectedActivity, setSelectedActivity] = useState<TSelectOption>(allActivitiesOption);
  const [selectedLocation, setSelectedLocation] = useState<TSelectOption>(allLocationsOption);
  const { activities, isLoadingActivities, locations, isLoadingLocations, allActivities } = useSearchQuery({ 
    activity: selectedActivity.value, 
    location: selectedLocation.value 
  });

  useEffect(() => {
    const experienceSlug = searchParams.get('experience');
    const countrySlug = searchParams.get('country');

    if (!isPreselectedRef.current && (!isLoadingActivities || !isLoadingLocations)) {
      if (experienceSlug && !isLoadingActivities) {
        const preselected = activities.find(
          (o) => o.value !== 0 && (o.slug ?? slugFromLabel(o.label)) === experienceSlug,
        );
        if (preselected) setSelectedActivity(preselected);
      }

      if (countrySlug && !isLoadingLocations) {
        const preselected = locations.find(
          (o) => o.value !== 0 && (o.slug ?? slugFromLabel(o.label)) === countrySlug,
        );
        if (preselected) setSelectedLocation(preselected);
      }
      isPreselectedRef.current = true;
    }
  }, [searchParams, isLoadingActivities, isLoadingLocations, activities, locations]);

  const handleSelectActivity = (option: SingleValue<TSelectOption>) => {
    setSelectedActivity(option ?? allActivitiesOption);
  };

  const handleSelectLocation = (option: SingleValue<TSelectOption>) => {
    setSelectedLocation(option ?? allLocationsOption);
  };

  return (
    <>
      <SearchDesktop
        activityList={activities}
        selectedActivity={selectedActivity}
        handleSelectActivity={handleSelectActivity}
        locationList={locations}
        selectedLocation={selectedLocation}
        handleSelectLocation={handleSelectLocation}
      />
      <SearchMobile
        activityList={activities}
        allActivities={allActivities}
        selectedActivity={selectedActivity}
        handleSelectActivity={handleSelectActivity}
        locationList={locations}
        selectedLocation={selectedLocation}
        handleSelectLocation={handleSelectLocation}
        locationsLoading={isLoadingLocations}
        activitiesLoading={isLoadingActivities}
      />
    </>
  );
};

export const Search = () => {
  return (
    <Suspense fallback={
      <div className="relative lg:absolute flex flex-row items-center justify-center w-full pb-3 lg:pb-0">
        <div className="w-full max-w-2xl h-12 bg-gray-800 rounded-full animate-pulse" />
      </div>
    }>
      <SearchContent />
    </Suspense>
  );
};